import { Fragment, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { api } from '../api/client';

export default function Permissions() {
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState({}); // `role:gate` -> bool
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api('/permissions')
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setEnabled(d.enabled || {});
      })
      .catch(() => !cancelled && setError('Could not load permissions.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const groups = useMemo(() => {
    if (!data) return [];
    const by = {};
    for (const g of data.gates) (by[g.group] || (by[g.group] = [])).push(g);
    return Object.entries(by);
  }, [data]);

  function isOn(role, gate) {
    if (role === 'admin') return true;
    return !!enabled[`${role}:${gate}`];
  }

  async function toggle(role, gate) {
    if (role === 'admin') return;
    const key = `${role}:${gate}`;
    const next = !enabled[key];
    setEnabled((e) => ({ ...e, [key]: next })); // optimistic
    setSavingKey(key);
    try {
      await api('/permissions', { method: 'PUT', body: { role, gate, enabled: next } });
    } catch {
      setEnabled((e) => ({ ...e, [key]: !next })); // revert
      setError('Could not save that change.');
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <>
      <PageHeader title="Roles & Permissions" />
      <section className="panel">
        {error && <div className="form-error" style={{ margin: 16 }}>{error}</div>}
        {loading || !data ? (
          <div className="perm-empty">Loading…</div>
        ) : (
          <div className="perm-wrap">
            <table className="perm">
              <thead>
                <tr>
                  <th className="perm__gate">Permission</th>
                  {data.roles.map((r) => (
                    <th key={r.key} className="perm__role">{r.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map(([group, gates]) => (
                  <Fragment key={group}>
                    <tr className="perm__grouprow">
                      <td colSpan={data.roles.length + 1}>{group}</td>
                    </tr>
                    {gates.map((g) => (
                      <tr key={g.key}>
                        <td className="perm__gate">{g.label}</td>
                        {data.roles.map((r) => {
                          const on = isOn(r.key, g.key);
                          const locked = r.key === 'admin';
                          const key = `${r.key}:${g.key}`;
                          return (
                            <td key={r.key} className="perm__cell">
                              <input
                                type="checkbox"
                                checked={on}
                                disabled={locked || savingKey === key}
                                onChange={() => toggle(r.key, g.key)}
                                aria-label={`${r.name}: ${g.label}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
            <p className="perm__note">Admin always has every permission. Changes apply immediately.</p>
          </div>
        )}
      </section>
    </>
  );
}

